import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Header from '@/components/ui/Header';
import ScheduleCalendar from './components/ScheduleCalendar';
import EmployeeSidebar from './components/EmployeeSidebar';
import ScheduleFilters from './components/ScheduleFilters';
import ShiftModal from './components/ShiftModal';

import Button from '@/components/ui/Button';
import { useAuth } from '@/context/AuthContext';
import { useLocations } from '@/context/LocationsContext';
import { fetchEmployees as fetchEmployeesFromApi } from '@/lib/api/employees';
import { fetchShifts, createShift, updateShift, deleteShift as deleteShiftFromApi } from '@/lib/api/shifts';
import LoadingState from '@/components/ui/LoadingState';
import useTranslation from '@/hooks/useTranslation';

const formatDateForApi = (input) => {
  if (!input) {
    return null;
  }
  const date = new Date(input);
  date.setHours(0, 0, 0, 0);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString().split('T')[0];
};

const normalizeToStartOfDay = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  date.setHours(0, 0, 0, 0);
  return date;
};

const EmployeeScheduling = () => {
  const { t } = useTranslation(['header', 'common', 'employeeScheduling']);
  const { user } = useAuth();
  const userRole = user?.role ?? 'manager';
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const today = new Date();
    const monday = new Date(today);
    monday?.setDate(today?.getDate() - today?.getDay() + 1);
    monday?.setHours(0, 0, 0, 0);
    return monday;
  });

  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  const {
    locations,
    loading: locationsLoading,
    error: locationsError,
    refetch: refetchLocations,
  } = useLocations();

  const [employees, setEmployees] = useState([]);
  const departments = useMemo(() => (
    Array.from(
      employees.reduce((acc, employee) => {
        if (employee?.department) {
          acc.add(employee.department);
        }
        return acc;
      }, new Set()),
    ).map((dept) => ({ id: dept, name: dept }))
  ), [employees]);

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [viewMode, setViewMode] = useState('week');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [shiftModal, setShiftModal] = useState({ isOpen: false, shift: null, mode: 'create' });
  const [employeeFilters, setEmployeeFilters] = useState({});
  const [shifts, setShifts] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [loadingShifts, setLoadingShifts] = useState(true);
  const [error, setError] = useState(null);
  const [lastShiftChange, setLastShiftChange] = useState(null);
  const [isRevertingChange, setIsRevertingChange] = useState(false);

  useEffect(() => {
    if (!Array.isArray(locations) || locations.length === 0) {
      return;
    }

    setSelectedLocation((prev) => {
      if (prev && locations.some((locationItem) => locationItem?.id === prev?.id)) {
        return prev;
      }
      return locations[0];
    });
  }, [locations]);

  const loading = loadingEmployees || loadingShifts || locationsLoading;
  const combinedError = error ?? locationsError;
  const isLocationFetchError = !error && !!locationsError;

  const hoursByEmployee = useMemo(() => {
    const map = new Map();
    shifts.forEach((shift) => {
      const startHour = parseInt(shift?.startTime?.split(':')?.[0], 10);
      const endHour = parseInt(shift?.endTime?.split(':')?.[0], 10);
      if (Number.isFinite(startHour) && Number.isFinite(endHour)) {
        const hours = Math.max(0, endHour - startHour);
        map.set(shift?.employeeId, (map.get(shift?.employeeId) ?? 0) + hours);
      }
    });
    return map;
  }, [shifts]);

  const enrichedEmployees = useMemo(
    () => employees.map((employee) => ({
      ...employee,
      hoursThisWeek: hoursByEmployee.get(employee?.id) ?? 0,
    })),
    [employees, hoursByEmployee],
  );

  const getWeekRange = useCallback((weekDate) => {
    const start = new Date(weekDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(0, 0, 0, 0);
    return {
      from: formatDateForApi(start),
      to: formatDateForApi(end),
    };
  }, []);

  const reloadEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    setError(null);
    try {
      const data = await fetchEmployeesFromApi();
      setEmployees(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  useEffect(() => {
    reloadEmployees();
  }, [reloadEmployees]);

  const reloadShifts = useCallback(async (weekDate, location) => {
    setLoadingShifts(true);
    setError(null);
    try {
      const { from, to } = getWeekRange(weekDate);
      const data = await fetchShifts({ from, to, locationId: location?.id });
      setShifts(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingShifts(false);
    }
  }, [getWeekRange]);

  useEffect(() => {
    if (!selectedWeek || !selectedLocation) {
      return;
    }
    reloadShifts(selectedWeek, selectedLocation);
  }, [reloadShifts, selectedLocation, selectedWeek]);

  const headerNotifications = useMemo(() => ({ messages: 0, timeOff: 0 }), []);

  const retryAll = useCallback(() => {
    if (locationsError) {
      refetchLocations().catch((refetchError) => {
        console.error('Failed to refetch locations', refetchError);
      });
    }
    reloadEmployees();
    if (selectedLocation) {
      reloadShifts(selectedWeek, selectedLocation);
    }
  }, [locationsError, refetchLocations, reloadEmployees, reloadShifts, selectedLocation, selectedWeek]);

  useEffect(() => {
    if (selectedDepartment !== 'all' && !departments.some((dept) => dept.id === selectedDepartment)) {
      setSelectedDepartment('all');
    }
  }, [departments, selectedDepartment]);

  const handleFiltersChange = useCallback((filters) => {
    setEmployeeFilters(filters);
  }, [setEmployeeFilters]);

  const handleWeekChange = useCallback((nextWeek) => {
    const normalized = normalizeToStartOfDay(nextWeek ?? new Date());
    if (!normalized) {
      return;
    }

    setSelectedWeek(normalized);
  }, []);

  const handleDateChange = useCallback((nextDate) => {
    const normalized = normalizeToStartOfDay(nextDate ?? new Date());
    if (!normalized) {
      return;
    }

    setSelectedDate(normalized);
  }, []);

  const filteredShifts = useMemo(() => {
    if (!Array.isArray(shifts)) {
      return [];
    }

    const { selectedStaffMembers, selectedRoles } = employeeFilters || {};

    return shifts.filter((shift) => {
      if (shift?.locationId !== selectedLocation?.id) {
        return false;
      }

      if (selectedStaffMembers && selectedStaffMembers[shift?.employeeId] === false) {
        return false;
      }

      if (selectedRoles && selectedRoles[shift?.role] === false) {
        return false;
      }

      return true;
    });
  }, [employeeFilters, selectedLocation?.id, shifts]);

  const totalHours = useMemo(() => {
    return filteredShifts?.reduce((total, shift) => {
      const start = parseInt(shift?.startTime?.split(':')?.[0], 10);
      const end = parseInt(shift?.endTime?.split(':')?.[0], 10);
      return total + Math.max(0, end - start);
    }, 0);
  }, [filteredShifts]);

  const laborCost = useMemo(() => {
    return filteredShifts?.reduce((total, shift) => {
      const start = parseInt(shift?.startTime?.split(':')?.[0], 10);
      const end = parseInt(shift?.endTime?.split(':')?.[0], 10);
      const hours = Math.max(0, end - start);
      return total + hours * (shift?.hourlyRate || 15);
    }, 0);
  }, [filteredShifts]);

  const buildUpdatePayload = useCallback(
    (shift, fallback = {}) => {
      const payload = {};

      const resolveString = (value) => (typeof value === 'string' ? value.trim() : '');

      const resolvedEmployeeId = resolveString(shift?.employeeId ?? fallback?.employeeId ?? '');
      if (resolvedEmployeeId) {
        payload.employeeId = resolvedEmployeeId;
      }

      const resolvedDate = resolveString(shift?.date ?? fallback?.date ?? '');
      if (resolvedDate) {
        payload.date = resolvedDate;
      }

      const resolvedStartTime = resolveString(shift?.startTime ?? fallback?.startTime ?? '');
      if (resolvedStartTime) {
        payload.startTime = resolvedStartTime;
      }

      const resolvedEndTime = resolveString(shift?.endTime ?? fallback?.endTime ?? '');
      if (resolvedEndTime) {
        payload.endTime = resolvedEndTime;
      }

      const resolvedLocationId = resolveString(
        shift?.locationId ?? fallback?.locationId ?? selectedLocation?.id ?? '',
      );
      if (resolvedLocationId) {
        payload.locationId = resolvedLocationId;
      }

      const resolvedStatus = resolveString(shift?.status ?? fallback?.status ?? '');
      if (resolvedStatus) {
        payload.status = resolvedStatus;
      }

      const resolvedRole = resolveString(shift?.role ?? fallback?.role ?? '');
      if (resolvedRole) {
        payload.role = resolvedRole;
      }

      if (typeof shift?.notes === 'string') {
        payload.notes = shift.notes;
      } else if (typeof fallback?.notes === 'string') {
        payload.notes = fallback.notes;
      }

      const resolvedHourlyRate =
        typeof shift?.hourlyRate === 'number'
          ? shift.hourlyRate
          : typeof fallback?.hourlyRate === 'number'
            ? fallback.hourlyRate
            : null;
      if (typeof resolvedHourlyRate === 'number' && Number.isFinite(resolvedHourlyRate)) {
        payload.hourlyRate = resolvedHourlyRate;
      }

      return payload;
    },
    [selectedLocation?.id],
  );

  const handleShiftUpdate = useCallback(
    async (updatedShift, originalShiftOverride = null) => {
      if (!updatedShift?.id) {
        return null;
      }

      const existingShift =
        originalShiftOverride ?? shifts.find((shift) => shift?.id === updatedShift.id) ?? null;
      const previousSnapshot = existingShift ? { ...existingShift } : null;
      const optimisticShift = existingShift ? { ...existingShift, ...updatedShift } : updatedShift;

      setShifts((prev) =>
        prev?.map((shift) =>
          shift?.id === updatedShift.id ? { ...shift, ...optimisticShift } : shift,
        ),
      );

      const payload = buildUpdatePayload(optimisticShift, previousSnapshot ?? {});

      try {
        const savedShift = await updateShift(updatedShift.id, payload);
        const normalizedShift = existingShift
          ? { ...existingShift, ...savedShift }
          : savedShift;

        setShifts((prev) =>
          prev?.map((shift) =>
            shift?.id === updatedShift.id ? { ...shift, ...normalizedShift } : shift,
          ),
        );

        if (previousSnapshot && originalShiftOverride) {
          setLastShiftChange({
            shiftId: updatedShift.id,
            previous: previousSnapshot,
            current: { ...previousSnapshot, ...normalizedShift },
          });
        } else if (!originalShiftOverride && lastShiftChange?.shiftId === updatedShift.id) {
          setLastShiftChange(null);
        }

        return normalizedShift;
      } catch (updateError) {
        if (previousSnapshot) {
          setShifts((prev) =>
            prev?.map((shift) =>
              shift?.id === updatedShift.id ? previousSnapshot : shift,
            ),
          );
        }
        throw updateError;
      }
    },
    [buildUpdatePayload, lastShiftChange, shifts],
  );

  const handleShiftCreate = useCallback((newShift) => {
    setShifts((prev) => [...prev, newShift]);
  }, []);

  const handleShiftSave = useCallback(async (shiftData) => {
    if (shiftData?.id && shifts?.some((s) => s?.id === shiftData.id)) {
      await handleShiftUpdate(shiftData);
      return;
    }

    if (!selectedLocation?.id) {
      const locationError = new Error('A location must be selected before assigning a shift.');
      throw locationError;
    }

    const employeeRecord = enrichedEmployees?.find((employee) => employee?.id === shiftData?.employeeId);

    const payload = {
      employeeId: shiftData?.employeeId,
      date: shiftData?.date,
      startTime: shiftData?.startTime,
      endTime: shiftData?.endTime,
      locationId: selectedLocation.id,
      status: 'scheduled',
    };

    if (shiftData?.notes) {
      payload.notes = shiftData.notes;
    }

    const resolvedRole = shiftData?.role ?? employeeRecord?.role;
    if (resolvedRole) {
      payload.role = resolvedRole;
    }

    const createdShift = await createShift(payload);
    const enhancedShift = {
      ...createdShift,
      employeeName: createdShift?.employeeName || employeeRecord?.name || shiftData?.employeeName || '',
      employeeId: createdShift?.employeeId ?? employeeRecord?.id ?? shiftData?.employeeId,
      role: createdShift?.role ?? resolvedRole ?? '',
      locationId: createdShift?.locationId ?? selectedLocation.id,
      notes: createdShift?.notes ?? payload.notes ?? '',
      status: createdShift?.status ?? 'scheduled',
    };
    handleShiftCreate(enhancedShift);
    await reloadShifts(selectedWeek, selectedLocation);
  }, [
    shifts,
    handleShiftUpdate,
    handleShiftCreate,
    selectedLocation,
    enrichedEmployees,
    reloadShifts,
    selectedWeek,
  ]);

  const handleShiftDelete = useCallback(
    async (shiftId) => {
      if (!shiftId) {
        return;
      }

      await deleteShiftFromApi(shiftId);

      setShifts((prev) => prev?.filter((shift) => shift?.id !== shiftId));
      setLastShiftChange((prev) => (prev?.shiftId === shiftId ? null : prev));

      if (selectedLocation && selectedWeek) {
        await reloadShifts(selectedWeek, selectedLocation);
      }
    },
    [reloadShifts, selectedLocation, selectedWeek],
  );

  const handleRevertLastChange = useCallback(async () => {
    if (!lastShiftChange) {
      return;
    }

    const { shiftId, previous, current } = lastShiftChange;
    if (!shiftId || !previous) {
      return;
    }

    setIsRevertingChange(true);
    setShifts((prev) => prev?.map((shift) => (shift?.id === shiftId ? { ...shift, ...previous } : shift)));

    try {
      const payload = buildUpdatePayload(previous, current ?? {});
      const reverted = await updateShift(shiftId, payload);
      const normalized = { ...previous, ...reverted };

      setShifts((prev) =>
        prev?.map((shift) => (shift?.id === shiftId ? { ...shift, ...normalized } : shift)),
      );
      setLastShiftChange(null);
    } catch (revertError) {
      console.error('Failed to revert the last shift change', revertError);
      setShifts((prev) =>
        prev?.map((shift) => (shift?.id === shiftId ? current ?? shift : shift)),
      );
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(
          t(
            'employeeScheduling.calendar.revertFailed',
            'We could not revert the shift change. Please try again.',
          ),
        );
      }
    } finally {
      setIsRevertingChange(false);
    }
  }, [buildUpdatePayload, lastShiftChange, t]);

  const openShiftModal = useCallback((shift = null, mode = 'create') => {
    setShiftModal({ isOpen: true, shift, mode });
  }, []);

  const handleShiftSelect = useCallback(
    (shift) => {
      if (!shift) {
        return;
      }
      openShiftModal(shift, 'edit');
    },
    [openShiftModal],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          userRole={userRole}
          selectedLocation={selectedLocation?.name}
          locations={locations}
          onLocationChange={setSelectedLocation}
          notifications={headerNotifications}
        />
        <div className="pt-16">
          <LoadingState
            message={t('employeeScheduling.page.loading')}
            heightClassName="min-h-[calc(100vh-4rem)]"
          />
        </div>
      </div>
    );
  }

  if (combinedError) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          userRole={userRole}
          selectedLocation={selectedLocation?.name}
          locations={locations}
          onLocationChange={setSelectedLocation}
          notifications={headerNotifications}
        />
        <div className="pt-16 flex flex-col items-center justify-center text-muted-foreground space-y-4">
          <p>{t('employeeScheduling.page.error')}</p>
          {(isLocationFetchError || combinedError) && (
            <p className="text-sm text-muted-foreground/80 max-w-md text-center">
              {isLocationFetchError
                ? (Array.isArray(locationsError?.response?.data?.message)
                    ? locationsError.response.data.message.join('. ')
                    : locationsError?.response?.data?.message || locationsError?.message || 'Unable to load locations.')
                : (Array.isArray(combinedError?.response?.data?.message)
                    ? combinedError.response.data.message.join('. ')
                    : combinedError?.response?.data?.message || combinedError?.message)}
            </p>
          )}
          <Button variant="outline" onClick={retryAll}>
            {t('employeeScheduling.page.retry')}
          </Button>
        </div>
      </div>
    );
  }

  const closeShiftModal = () => {
    setShiftModal({ isOpen: false, shift: null, mode: 'create' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        userRole={userRole}
        selectedLocation={selectedLocation?.name}
        locations={locations}
        onLocationChange={setSelectedLocation}
        notifications={headerNotifications} />

      <div className="pt-16 flex" style={{ height: 'calc(100vh - 64px)' }}>
        {/* Employee Sidebar */}
        <EmployeeSidebar
          employees={enrichedEmployees}
          onEmployeeSelect={setSelectedEmployee}
          selectedEmployee={selectedEmployee}
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          filters={employeeFilters}
          onFiltersChange={handleFiltersChange} />


        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="p-6 flex-1 space-y-6 overflow-auto">
            {/* Filters */}
            <ScheduleFilters
              locations={locations}
              selectedLocation={selectedLocation}
              onLocationChange={setSelectedLocation}
              departments={departments}
              selectedDepartment={selectedDepartment}
              onDepartmentChange={setSelectedDepartment}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              onRefresh={() => reloadShifts(selectedWeek, selectedLocation)}
              totalShifts={filteredShifts?.length ?? 0}
              totalHours={totalHours}
              laborCost={laborCost} />

            {/* Action Bar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Button
                  variant="default"
                  onClick={() => openShiftModal(null, 'create')}
                  iconName="Plus"
                  iconSize={16}
                >
                  {t('employeeScheduling.page.actions.assign')}
                </Button>
              </div>

              <div className="flex items-center space-x-3">
                {lastShiftChange && (
                  <Button
                    variant="outline"
                    iconName="RotateCcw"
                    iconSize={16}
                    onClick={handleRevertLastChange}
                    disabled={isRevertingChange}
                  >
                    {isRevertingChange
                      ? t('employeeScheduling.calendar.reverting', 'Reverting...')
                      : t('employeeScheduling.calendar.revertChange', 'Revert Change')}
                  </Button>
                )}
                <Button
                  variant="success"
                  iconName="Send"
                  iconSize={16}
                >
                  {t('employeeScheduling.page.actions.publish')}
                </Button>
              </div>
            </div>

            {/* Calendar */}
            <ScheduleCalendar
              selectedWeek={selectedWeek}
              onWeekChange={handleWeekChange}
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              shifts={shifts}
              openingTimes={selectedLocation?.openingTimes ?? null}
              onShiftUpdate={handleShiftUpdate}
              onShiftSelect={handleShiftSelect}
              employees={enrichedEmployees}
              selectedLocation={selectedLocation}
              viewMode={viewMode}
              employeeFilters={employeeFilters}
            />

          </div>
        </div>
      </div>
      
      {/* Shift Modal */}
      <ShiftModal
        isOpen={shiftModal?.isOpen}
        onClose={closeShiftModal}
        shift={shiftModal?.shift}
        employees={enrichedEmployees}
        onSave={handleShiftSave}
        onDelete={handleShiftDelete}
        mode={shiftModal?.mode} />

    </div>);

};

export default EmployeeScheduling;
